import React, { useCallback, useEffect, useState } from 'react';
import {
  getDeliverySettings,
  listVehicleTypes,
  updateDeliverySettings,
  updateVehicleType,
} from '../../services/adminDeliverySettingsService';
import { deliveryOn } from './feeMath';
import './VehicleRates.css';

const tzs = (n) => `TZS ${Math.round(Number(n) || 0).toLocaleString('en-TZ')}`;

/** A journey worth checking a rate against. */
const EXAMPLE_KM = 7;

/**
 * What every delivery costs, in one table: the vehicles.
 *
 * There used to be two sets of numbers — what the buyer was charged and what
 * a driver was offered — and they could say different things about the same
 * journey. Each vehicle now carries what it costs to send out, per kilometre
 * and per kilogram, and the weight of an order decides which one takes it.
 * That vehicle's price is what the buyer pays and what the driver earns.
 */
const VehicleRates = ({ onToast }) => {
  const [vehicles, setVehicles] = useState([]);
  const [settings, setSettings] = useState({ minFee: '', missingLocationFee: '' });
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [error, setError] = useState(null);

  const toast = useCallback((message, type = 'success') => {
    if (onToast) onToast(message, type);
  }, [onToast]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [fleet, global] = await Promise.all([
        listVehicleTypes(false),
        getDeliverySettings(),
      ]);

      // Lightest first: the first vehicle big enough takes the order, so this
      // is the order the choice is actually made in.
      const sorted = [...(fleet || [])].sort(
        (a, b) => (Number(a.maxWeightKg) || 0) - (Number(b.maxWeightKg) || 0));

      setVehicles(sorted.map(v => ({
        id: v.$id,
        name: v.name,
        code: v.code,
        isActive: v.isActive !== false,
        baseFee: String(v.baseFee ?? ''),
        pricePerKm: String(v.pricePerKm ?? ''),
        pricePerKg: String(v.pricePerKg ?? ''),
        maxWeightKg: String(v.maxWeightKg ?? ''),
      })));

      if (global) {
        setSettings({
          minFee: String(global.minFee ?? ''),
          missingLocationFee: String(global.buyerMissingLocationFee ?? ''),
        });
      }
    } catch (e) {
      setError(e.message || 'Could not load the delivery rates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const edit = (id, field, value) => {
    setVehicles(prev => prev.map(v => (v.id === id ? { ...v, [field]: value } : v)));
  };

  const saveVehicle = async (vehicle) => {
    setSavingId(vehicle.id);
    try {
      await updateVehicleType(vehicle.id, {
        baseFee: vehicle.baseFee,
        pricePerKm: vehicle.pricePerKm,
        pricePerKg: vehicle.pricePerKg,
        maxWeightKg: vehicle.maxWeightKg,
        isActive: vehicle.isActive,
      });
      toast(`${vehicle.name} saved. Buyers and drivers see this within half an hour.`);
      load();
    } catch (e) {
      toast(e.message || 'Could not save this vehicle', 'error');
    } finally {
      setSavingId(null);
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await updateDeliverySettings({
        minFee: settings.minFee,
        buyerMissingLocationFee: settings.missingLocationFee,
      });
      toast('Saved.');
      load();
    } catch (e) {
      toast(e.message || 'Could not save', 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading) return <div className="vehicle-rates-loading">Loading rates…</div>;

  return (
    <div className="vehicle-rates">
      {error && <div className="vehicle-rates-error">{error}</div>}

      <div className="vehicle-rates-intro">
        <h3>What a delivery costs</h3>
        <p>
          One price per journey: an order's weight picks the lightest vehicle
          that can carry it, and that vehicle's rates are both what the buyer
          pays at checkout and what the driver is offered for the job.
        </p>
      </div>

      <div className="vehicle-grid">
        {vehicles.map(vehicle => {
          // Priced at the heaviest load this vehicle is chosen for, which is
          // where its rates matter most.
          const example = deliveryOn({
            baseFee: vehicle.baseFee,
            perKm: vehicle.pricePerKm,
            perKg: vehicle.pricePerKg,
            km: EXAMPLE_KM,
            weightKg: vehicle.maxWeightKg,
            minFee: settings.minFee,
          });

          return (
            <div className={`vehicle-card ${vehicle.isActive ? '' : 'is-off'}`}
                 key={vehicle.id}>
              <div className="vehicle-card-head">
                <h4>{vehicle.name}</h4>
                <label className="vehicle-active">
                  <input
                    type="checkbox"
                    checked={vehicle.isActive}
                    onChange={(e) => edit(vehicle.id, 'isActive', e.target.checked)}
                  />
                  <span>{vehicle.isActive ? 'In use' : 'Off'}</span>
                </label>
              </div>

              <div className="vehicle-fields">
                <label>
                  <span>Sending it out (TZS)</span>
                  <input type="number" min="0" value={vehicle.baseFee}
                         onChange={(e) => edit(vehicle.id, 'baseFee', e.target.value)} />
                </label>
                <label>
                  <span>Per kilometre (TZS)</span>
                  <input type="number" min="0" value={vehicle.pricePerKm}
                         onChange={(e) => edit(vehicle.id, 'pricePerKm', e.target.value)} />
                </label>
                <label>
                  <span>Per kilogram (TZS)</span>
                  <input type="number" min="0" value={vehicle.pricePerKg}
                         onChange={(e) => edit(vehicle.id, 'pricePerKg', e.target.value)} />
                </label>
                <label>
                  <span>Carries up to (kg)</span>
                  <input type="number" min="0" value={vehicle.maxWeightKg}
                         onChange={(e) => edit(vehicle.id, 'maxWeightKg', e.target.value)} />
                </label>
              </div>

              <p className="vehicle-example">
                A full load ({vehicle.maxWeightKg || 0} kg) over {EXAMPLE_KM} km:{' '}
                <strong>{tzs(example)}</strong>
              </p>

              <button type="button" className="vehicle-save"
                      onClick={() => saveVehicle(vehicle)}
                      disabled={savingId === vehicle.id}>
                {savingId === vehicle.id ? 'Saving…' : `Save ${vehicle.name}`}
              </button>
            </div>
          );
        })}
      </div>

      <form className="vehicle-card vehicle-global" onSubmit={saveSettings}>
        <div className="vehicle-card-head">
          <h4>Whatever the vehicle</h4>
        </div>
        <div className="vehicle-fields">
          <label>
            <span>Minimum charge (TZS)</span>
            <input type="number" min="0" value={settings.minFee}
                   onChange={(e) => setSettings(s => ({ ...s, minFee: e.target.value }))} />
          </label>
          <label>
            <span>When a shop has no location (TZS)</span>
            <input type="number" min="0" value={settings.missingLocationFee}
                   onChange={(e) => setSettings(s => ({ ...s, missingLocationFee: e.target.value }))} />
          </label>
        </div>
        <p className="vehicle-note">
          No delivery is billed below the minimum. The second is charged when a
          shop has no coordinates saved, so the distance cannot be measured and
          checkout would otherwise stop — the buyer is told it is an estimate.
        </p>
        <button type="submit" className="vehicle-save" disabled={savingSettings}>
          {savingSettings ? 'Saving…' : 'Save'}
        </button>
      </form>
    </div>
  );
};

export default VehicleRates;
